import { useMutation } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import xor from 'lodash/xor';
import { useState } from 'react';
import styled from 'styled-components';

import { color, dimension } from '@creditornot/cb-ingredients/design-tokens';
import {
  DelistingRecommendationsApproveRequest,
  DelistingRecommendationsBulkApproveRequest,
  DelistingRecommendationsBulkRejectRequest,
  DelistingRecommendationsRejectRequest,
  DelistingReason as DelistingReasonType,
  DelistingRecommendationsBulkActionFilter,
} from 'common/api-types/recommendations';
import { ContentContainer } from 'common/components/cookbook/ContentContainer';
import { SelectionState } from 'common/components/cookbook/DataTable/types';
import { SortButton } from 'common/components/cookbook/SortButton';
import { Stack } from 'common/components/cookbook/Stack';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';
import { useSetSearch } from 'common/hooks/useSetSearch';
import { useApiQueryClient } from 'common/query/useApiQueryClient';
import { AxiosDelistingRecommendationsError, LocalizedMessageId } from 'common/types';
import { downloadBlob } from 'common/utils/downloadUtils';
import {
  selectionRecommendationsRouteApi,
  SelectionRecommendationsSearchRouteSchemaType,
} from 'features/selection-recommendations';
import {
  approveDelistingRecommendations,
  bulkApproveDelistingRecommendations,
  bulkRejectDelistingRecommendations,
  rejectDelistingRecommendations,
} from 'features/selection-recommendations/api';
import { FilterSelect } from 'features/selection-recommendations/common/FilterSelect/FilterSelect';
import { ActionSelect } from 'features/selection-recommendations/common/RecommendationActions/ActionSelect';
import { ApproveRecommendationModal } from 'features/selection-recommendations/common/RecommendationActions/DownloadApproved/ApproveRecommendationModal';
import {
  DELISTING_RECOMMENDATIONS_QUERY_KEY_ROOT,
  useDelistingRecommendationsInfiniteQuery,
} from 'features/selection-recommendations/query';
import { DelistingReasons } from 'features/selection-recommendations/Recommendations/DelistingRecommendations/DelistingReasons';
import { sortParamMap } from 'features/selection-recommendations/Recommendations/DelistingRecommendations/sortParamMap';
import { RecommendationAction } from 'features/selection-recommendations/types';

import { DelistingRecommendationsList } from './DelistingRecommendationsList';
import { downloadableReasons, getRecommendationItems } from './utils';
import { selectedProductFamilyIdsAtom, selectedRecommendationsAtom } from '../state';
import { RejectDelistingsForm } from './RejectRecommendations/RejectDelistingsForm';
import {
  DelistingRecommendationRejectionFormValues,
  DelistingRecommendationsSortType,
} from './types';

const Header = styled(ContentContainer)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${dimension.spaceRegularXlarge};
  overflow: auto;
`;

const HorizontalDivider = styled.div`
  height: 20px;
  border-left: 2px solid ${color.border};
`;

export const DelistingRecommendations = () => {
  const [countryCode] = useSelectedCountryCodes();
  const selectedProductFamilyIds = useAtomValue(selectedProductFamilyIdsAtom);
  const setSearch = useSetSearch<SelectionRecommendationsSearchRouteSchemaType>();
  const f = useFormatMessage();

  const {
    delistingsSortBy = 'EXPECTED_BUSINESS_IMPACT_DESC',
    venueIds = [],
    brandIds = [],
    delistingReasons = [],
    vendorIds = [],
  } = selectionRecommendationsRouteApi.useSearch();

  const sortParams = sortParamMap[delistingsSortBy];
  const sortingDirection = sortParams.sortingDirection;

  const {
    data,
    error,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isLoading,
    delistingRecommendations,
    totalCount,
  } = useDelistingRecommendationsInfiniteQuery(countryCode, {
    delistingRecommendationsSortBy: sortParams.sortBy,
    sortingDirection,
    pageSize: 100,
    filter: {
      productFamilyIds: selectedProductFamilyIds,
      brandIds,
      venueIds,
      reasons: delistingReasons,
      vendorIds,
    },
  });

  const [selectedState, setSelectedState] = useAtom(selectedRecommendationsAtom);
  const { bulkSelect, unselected, selected } = selectedState;

  const [action, setAction] = useState<RecommendationAction | null>(null);
  const queryClient = useApiQueryClient();

  const resetSelectionState = () => {
    setSelectedState({ bulkSelect: false, selected: [], unselected: [] });
    setAction(null);
  };

  const invalidateQuery = async () => {
    resetSelectionState();
    await queryClient.invalidateQueries({
      queryKey: [DELISTING_RECOMMENDATIONS_QUERY_KEY_ROOT],
    });
  };

  const onDownloadSuccess = async (blob: Blob) => {
    const fileName = 'delisting_recommendations.csv';
    downloadBlob(new Blob([blob]), fileName);
    await invalidateQuery();
  };

  const onDownloadRejectedSuccess = async (blob: Blob) => {
    const fileName = 'delisting_recommendations_rejected.csv';
    downloadBlob(new Blob([blob]), fileName);
    await invalidateQuery();
  };

  const {
    mutate: approveMutate,
    isPending: approveIsPending,
    error: approveError,
  } = useMutation<Blob, AxiosDelistingRecommendationsError, DelistingRecommendationsApproveRequest>(
    {
      mutationFn: approveDelistingRecommendations,
      onSuccess: onDownloadSuccess,
    },
  );

  const {
    mutate: bulkApproveMutate,
    isPending: bulkApproveIsPending,
    error: bulkApproveError,
  } = useMutation<
    Blob,
    AxiosDelistingRecommendationsError,
    DelistingRecommendationsBulkApproveRequest
  >({
    mutationFn: bulkApproveDelistingRecommendations,
    onSuccess: onDownloadSuccess,
  });

  const {
    mutate: rejectMutate,
    isPending: rejectIsPending,
    error: rejectError,
  } = useMutation<Blob, AxiosDelistingRecommendationsError, DelistingRecommendationsRejectRequest>({
    mutationFn: rejectDelistingRecommendations,
    onSuccess: async (blob, request) => {
      if (downloadableReasons.includes(request.reason)) {
        await onDownloadRejectedSuccess(blob);
        return;
      }

      await invalidateQuery();
    },
  });

  const {
    mutate: bulkRejectMutate,
    isPending: bulkRejectIsPending,
    error: bulkRejectError,
  } = useMutation<
    Blob,
    AxiosDelistingRecommendationsError,
    DelistingRecommendationsBulkRejectRequest
  >({
    mutationFn: bulkRejectDelistingRecommendations,
    onSuccess: async (blob, request) => {
      if (downloadableReasons.includes(request.reason)) {
        await onDownloadRejectedSuccess(blob);
        return;
      }

      await invalidateQuery();
    },
  });

  const requestedAt = data?.pages[0]?.meta.requestedAt;

  const onRowSelectChange = (value: 'ALL' | SelectionState) => {
    setSelectedState(value === 'ALL' ? { bulkSelect: true, selected: [], unselected: [] } : value);
  };

  const selectedIds = selected.flatMap((current) => current.selectedSubrows ?? []);

  const selectedRecommendationCount = bulkSelect
    ? totalCount - unselected.length
    : selectedIds.length;

  const handleDownload = () => {
    if (!requestedAt) {
      return;
    }

    if (!bulkSelect) {
      const recommendationItems = getRecommendationItems(selectedIds, delistingRecommendations);
      const recommendations = {
        delistingRecommendationItems: recommendationItems,
      };

      approveMutate({
        recommendations,
        countryCode,
      });

      return;
    }

    const unselectedRecommendationItems = getRecommendationItems(
      unselected,
      delistingRecommendations,
    );

    const bulkActionFilter: DelistingRecommendationsBulkActionFilter = {
      countryCode,
      excludedRecommendationItems: unselectedRecommendationItems,
      venueIds,
      brandIds,
      reasons: delistingReasons,
      vendorIds,
      productFamilyIds: selectedProductFamilyIds,
    };

    bulkApproveMutate({
      filter: bulkActionFilter,
      meta: {
        expectedActionsCount: selectedRecommendationCount,
        recommendationsRequestedAt: requestedAt,
      },
    });
  };

  const handleReject = (values: DelistingRecommendationRejectionFormValues) => {
    if (!requestedAt) {
      return;
    }

    if (!bulkSelect) {
      const selectedRecommendationItems = getRecommendationItems(
        selectedIds,
        delistingRecommendations,
      );

      const recommendations = {
        delistingRecommendationItems: selectedRecommendationItems,
      };

      rejectMutate({
        recommendations,
        countryCode,
        reason: values.reason,
        comment: values.comment,
      });

      return;
    }

    const unselectedRecommendationItems = getRecommendationItems(
      unselected,
      delistingRecommendations,
    );

    const bulkActionFilter: DelistingRecommendationsBulkActionFilter = {
      countryCode,
      excludedRecommendationItems: unselectedRecommendationItems,
      venueIds,
      brandIds,
      reasons: delistingReasons,
      vendorIds,
      productFamilyIds: selectedProductFamilyIds,
    };

    bulkRejectMutate({
      filter: bulkActionFilter,
      meta: {
        expectedActionsCount: selectedRecommendationCount,
        recommendationsRequestedAt: requestedAt,
      },
      reason: values.reason,
      comment: values.comment,
    });
  };

  const handleSortBy = async (sortBy: DelistingRecommendationsSortType) => {
    await resetSelectionState();
    setSearch({ delistingsSortBy: sortBy });
  };

  const options = Object.entries(sortParamMap).map(([key]) => ({
    label: f(`selection_recommendations.sort_by:${key}` as LocalizedMessageId),
    sortBy: key as DelistingRecommendationsSortType,
    sortingType: sortingDirection,
  }));

  const rejectDelistingRecommendationError = rejectError || bulkRejectError;
  const approveDelistingRecommendationError = approveError || bulkApproveError;

  const handleChangeReason = (reason: DelistingReasonType) => {
    setSearch({ delistingReasons: xor(delistingReasons, [reason]) });
  };

  return (
    <>
      <Header borderBottom={true} verticalPadding="sm">
        <Stack direction="row" gap="regularXlarge" alignItems="center">
          <SortButton
            sortBy={delistingsSortBy}
            options={options}
            onSortChange={handleSortBy}
            sortingType={sortingDirection}
          />
          <HorizontalDivider />
        </Stack>
        <FilterSelect
          resetSelectionState={resetSelectionState}
          filters={['venue', 'brand', 'reason', 'vendor']}
          reasonFilter={
            <DelistingReasons
              onReasonChange={handleChangeReason}
              selectedReasons={delistingReasons}
            />
          }
        />
      </Header>
      <DelistingRecommendationsList
        error={error}
        groupedByProduct={delistingRecommendations}
        rowSelection={{
          onRowSelectChange,
          selectedRows: bulkSelect && !unselected.length ? 'ALL' : selectedIds,
        }}
        isFetching={isFetching}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isLoading={isLoading}
        totalCount={totalCount}
      />
      <ActionSelect
        approvalIsLoading={approveIsPending}
        isVisible={!!selectedRecommendationCount}
        handleRejectionModalOpen={() => {
          setAction('REJECT_RECOMMENDATIONS');
        }}
        handleApprovalModalOpen={() => {
          setAction('APPROVE_RECOMMENDATIONS');
        }}
      />
      <RejectDelistingsForm
        isOpen={action === 'REJECT_RECOMMENDATIONS'}
        onDismiss={() => setAction(null)}
        onReject={handleReject}
        rejectIsPending={rejectIsPending || bulkRejectIsPending}
        selectedRecommendationCount={selectedRecommendationCount}
        error={rejectDelistingRecommendationError}
      />
      <ApproveRecommendationModal
        isOpen={action === 'APPROVE_RECOMMENDATIONS'}
        onDismiss={() => setAction(null)}
        isDownloading={approveIsPending || bulkApproveIsPending}
        onDownload={handleDownload}
        error={approveDelistingRecommendationError}
        description={f('selection_recommendations.approve_delisting_recommendation_description', {
          count: selectedRecommendationCount,
        })}
      />
    </>
  );
};
